import { Thumbtack } from '@assets/svgs';
import { Dialog } from '@components/dialog/Dialog';
import { NotesList } from '@components/notesDialog/components/NotesList';
import { NoteSearchResponse } from '@lio/api-types';
import { SvgIcon, Avatar, Typography, DialogContent } from '@mui/material';
import { Box, darken, Theme } from '@mui/system';
import { useState } from 'react';

export type PinnedNotesBarProps = {
  pinnedNotes: NoteSearchResponse['items'];
};

export const PinnedNotesBar = ({ pinnedNotes }: PinnedNotesBarProps) => {
  const [pinnedDialogOpen, setPinnedDialogOpen] = useState(false);

  if (pinnedNotes?.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={sxProps.pinned.bar} onClick={() => setPinnedDialogOpen(true)} data-testid='pinned-notes-bar'>
        {pinnedNotes.slice(0, 1).map((n) => (
          <Box key={n.id} sx={sxProps.pinned.previewItem}>
            <SvgIcon
              sx={{
                height: 20,
                width: 20,
                color: 'text.main',
                transform: 'rotate(45deg) translateY(3px) translateX(2px)',
              }}
            >
              <Thumbtack />
            </SvgIcon>
            <Avatar sx={sxProps.pinned.avatar} />
            <Typography variant='caption' sx={sxProps.pinned.previewText}>
              {(n as any).rawText}
            </Typography>
          </Box>
        ))}
        {pinnedNotes.length > 1 && (
          <Typography variant='caption' color='text.secondary'>
            +{pinnedNotes.length - 1} more
          </Typography>
        )}
      </Box>
      {/* Pinned Notes Dialog */}
      <Dialog
        title='Pinned Notes'
        open={pinnedDialogOpen}
        handleCloseDialog={() => setPinnedDialogOpen(false)}
        onClose={() => setPinnedDialogOpen(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogContent dividers sx={{ p: 1, display: 'flex', flexDirection: 'column', maxHeight: 500 }}>
          <NotesList notes={pinnedNotes} />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Consolidated style object to keep JSX clean
const sxProps = {
  pinned: {
    avatar: { bgcolor: 'grey.300', width: 20, height: 20, mr: 0.5 },
    bar: {
      position: 'sticky',
      display: 'flex',
      px: 1,
      py: 0.75,
      alignItems: 'center',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
      mb: 0,
      cursor: 'pointer',

      '&:hover': { backgroundColor: (theme: Theme) => darken(theme.palette.background.paper, 0.1) },
    },
    previewItem: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 0.5,
      flex: 1,
      minWidth: 0,
    },
    author: { fontWeight: 600, color: 'text.secondary' },
    previewText: {
      display: 'block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      mt: 0.25,
      pr: 1,
    },
  },
} as const;
